/**
 * Developer Portal Page
 *
 * Renders the API reference inside the app shell, where the signed-in user's
 * token is available — the API's own /docs route sits behind `authenticate`,
 * which needs a header a browser navigation never sends.
 *
 * The page is deliberately thin. The reference itself already shows the API
 * title, version, description, authentication scheme, server/base URL and a
 * download link, so duplicating any of that here would just cost vertical
 * space. What remains is the one thing the reference cannot do: link into the
 * app to actually manage credentials.
 */

import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/button';
import { Loader2, Key, Webhook } from 'lucide-react';
import { API_URL } from '@/config/env';

/**
 * Code-split: the renderer carries Scalar's own (Vue-based) stack and
 * stylesheet, ~2.9 MB, which must not sit in the entry chunk for every page.
 */
const ScalarReference = lazy(() => import('@/components/developer/ScalarReference'));

const API_BASE_URL = API_URL;

export default function DeveloperPortal() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [openApiSpec, setOpenApiSpec] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadOpenApiSpec = useCallback(async () => {
    if (!currentUser) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/docs/openapi.json`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to load API spec: ${response.status}`);
      }

      setOpenApiSpec(await response.json());
    } catch (err: any) {
      console.error('Error loading OpenAPI spec:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadOpenApiSpec();
  }, [loadOpenApiSpec]);

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="mt-2 text-muted-foreground">Loading API documentation…</p>
          </div>
        </div>
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell>
        <Card className="border-destructive/50 bg-destructive/10">
          <CardHeader>
            <CardTitle className="text-destructive">Error Loading Documentation</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={loadOpenApiSpec} variant="outline">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </AppShell>
    );
  }

  return (
    <AppShell>
      {/* One slim row: page identity plus the two actions the reference can't
          perform. Everything else the old header showed (base URL, auth
          scheme, version) is rendered by the reference below. */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-xl font-semibold text-foreground">Developer Portal</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/settings?tab=api-keys')}>
            <Key className="h-4 w-4 mr-2" />
            API keys
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/settings?tab=webhooks')}>
            <Webhook className="h-4 w-4 mr-2" />
            Webhooks
          </Button>
        </div>
      </div>

      <Suspense
        fallback={
          <div className="flex items-center justify-center h-96 rounded-lg border border-border text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading API reference…
          </div>
        }
      >
        <ScalarReference spec={openApiSpec} />
      </Suspense>
    </AppShell>
  );
}
